import * as Sentry from '@sentry/react';
import { EmptyState } from '@vidacamara/orange-ui';
import { AUTH_ROLES_LOCAL_STORAGE, getSecureLocalStorageItem } from '@vidacamara/orange-utils';
import { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';

interface AuthRolesClaim {
	businessRoles: string[];
	coreRoles: string[];
}

const readGrantedRoles = (): string[] => {
	try {
		const storedClaim = getSecureLocalStorageItem(AUTH_ROLES_LOCAL_STORAGE);
		if (!storedClaim) return [];

		const { businessRoles, coreRoles } = JSON.parse(storedClaim) as AuthRolesClaim;

		return [...(Array.isArray(businessRoles) ? businessRoles : []), ...(Array.isArray(coreRoles) ? coreRoles : [])];
	} catch {
		Sentry.captureMessage('Access restriction claim could not be read');

		return [];
	}
};

export function withAccessRestriction<P extends object>(
	Component: ComponentType<P>,
	roles: string[],
): ComponentType<P> {
	const WithAccessRestriction = (props: P) => {
		const { t } = useTranslation();
		const hasAccess = readGrantedRoles().some((grantedRole) => roles.includes(grantedRole));

		if (!hasAccess) {
			return (
				<div className="flex h-[calc(100vh-300px)] min-h-[560px] items-center justify-center">
					<EmptyState
						illustration="ViewLockIllustration"
						title={t('documents.accessDenied.title')}
						description={t('documents.accessDenied.description')}
					/>
				</div>
			);
		}

		return <Component {...props} />;
	};

	WithAccessRestriction.displayName = `withAccessRestriction(${Component.displayName ?? Component.name})`;

	return WithAccessRestriction;
}
